import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDOB } from '../utils/dateUtils';
import { Lookup } from '../types/lookup';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Button, 
  Alert, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';

interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  dob: string;
  email: string;
  phone: string;
  address: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  contractUrl?: string;
  termsAccepted?: boolean;
  termsDeclined?: boolean;
  termsIssued?: boolean;
  paid?: boolean;
  acceptedAt?: Date;
  declinedAt?: Date;
  declineReason?: string;
  referralType?: string;
  organisation?: string;
  organisationDisplayName?: string;
  sessionRate?: number;
  numberOfSessions?: number;
  referralCategory?: string;
  gender?: string;
}

function ContractPage() {
  const { token, clientId } = useParams<{ token: string, clientId: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [, setAccepted] = useState(false);
  const [, setDeclined] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedClient, setEditedClient] = useState<Partial<Client>>({});
  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [genderOptions, setGenderOptions] = useState<Lookup[]>([]);
  const [showPaymentOptionsDialog, setShowPaymentOptionsDialog] = useState(false);
  const [paymentOption, setPaymentOption] = useState<'session' | 'block'>('session');
  const [numberOfSessions, setNumberOfSessions] = useState<number>(2);
  const [stripePublicKey, setStripePublicKey] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!token && !clientId) {
          setError('No contract token or client ID provided');
          setLoading(false);
          return;
        }

        // Get payment config to load Stripe key
        console.log('Fetching payment config...');
        const paymentConfigResponse = await axios.get('/api/payments/config');
        console.log('Payment config response:', paymentConfigResponse);

        const configData = paymentConfigResponse.data?.data;
        if (!configData) {
          console.error('No config data in response:', paymentConfigResponse);
          throw new Error('Payment configuration not found');
        }

        const stripeKey = configData.stripeConfig?.publishableKey;
        if (!stripeKey) {
          console.error('No Stripe key in config:', configData);
          throw new Error('Stripe key not configured');
        }

        console.log('Successfully loaded Stripe key');
        setStripePublicKey(stripeKey);

        let clientData: any = null;

        // First try to get contract data using the token
        if (token) {
          try {
            const contractResponse = await axios.get(`/api/contract/public/view/${token}`);
            clientData = contractResponse.data.client;
            
            // Ensure we have valid client data before proceeding
            if (!clientData) {
              setError('No client data received. Please contact your counselor for assistance.');
              setLoading(false);
              return;
            }
            
            setClient(clientData);

            // Initialize date values from dob if it exists
            if (clientData?.dob) {
              try {
                const dobDate = new Date(clientData.dob);
                if (!isNaN(dobDate.getTime())) {  // Check if date is valid
                  setYear(dobDate.getFullYear().toString());
                  setMonth((dobDate.getMonth() + 1).toString().padStart(2, '0'));
                  setDay(dobDate.getDate().toString().padStart(2, '0'));
                } else {
                  console.warn('Invalid DOB date format:', clientData.dob);
                }
              } catch (err) {
                console.error('Error parsing DOB:', err);
              }
            }

            // Then get the PDF URL using the client ID
            const pdfResponse = await axios.get(`/api/contract/public/get-presigned-url/${clientData._id}`);
            setPdfUrl(pdfResponse.data.url);

            // Get gender options
            const genderResponse = await axios.get('/api/lookups/public/gender');
            setGenderOptions(genderResponse.data);

            setEditedClient({
              firstName: clientData.firstName,
              lastName: clientData.lastName,
              phone: clientData.phone,
              gender: clientData.gender,
              emergencyContactName: clientData.emergencyContactName,
              emergencyContactPhone: clientData.emergencyContactPhone,
              address: clientData.address,
            });
          } catch (err: any) {
            if (err.response?.data?.status === 'expired') {
              navigate('/contract-link-expired');
              return;
            }
            console.error('Error fetching contract data:', err);
            setError(err.response?.data?.message || 'Error loading contract');
            setLoading(false);
            return;
          }
        } else if (clientId) {
          try {
            const clientResponse = await axios.get(`/api/clients/${clientId}`);
            clientData = clientResponse.data;
            
            // Ensure we have valid client data before proceeding
            if (!clientData) {
              setError('No client data received. Please contact your counselor for assistance.');
              setLoading(false);
              return;
            }
            
            setClient(clientData);

            // Initialize date values from dob if it exists
            if (clientData?.dob) {
              try {
                const dobDate = new Date(clientData.dob);
                if (!isNaN(dobDate.getTime())) {  // Check if date is valid
                  setYear(dobDate.getFullYear().toString());
                  setMonth((dobDate.getMonth() + 1).toString().padStart(2, '0'));
                  setDay(dobDate.getDate().toString().padStart(2, '0'));
                } else {
                  console.warn('Invalid DOB date format:', clientData.dob);
                }
              } catch (err) {
                console.error('Error parsing DOB:', err);
              }
            }

            // Get gender options
            const genderResponse = await axios.get('/api/lookups/public/gender');
            setGenderOptions(genderResponse.data);

            setEditedClient({
              firstName: clientData.firstName,
              lastName: clientData.lastName,
              phone: clientData.phone,
              gender: clientData.gender,
              emergencyContactName: clientData.emergencyContactName,
              emergencyContactPhone: clientData.emergencyContactPhone,
              address: clientData.address,
            });
          } catch (err: any) {
            setError('Error loading client data. Please try again or contact your counselor.');
            console.error('Error fetching client data:', err);
            setLoading(false);
            return;
          }
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching contract data:', err);
        const errorMessage = err.response?.data?.message || 'Failed to load contract data. Please try again.';
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchData();
  }, [token, clientId, navigate]);

  const handleSave = async () => {
    try {
      if (!client) return;

      const updatedClient = {
        ...client,
        ...editedClient,
        dob: `${year}-${month}-${day}`
      };

      const response = await axios.put(`/api/contract/public/update-details/${token}`, updatedClient);
      setClient(response.data.client);
      setIsEditing(false);
      setError(null);
    } catch (err: any) {
      console.error('Error updating client details:', err);
      setError(err.response?.data?.message || 'Failed to update client information. Please try again.');
    }
  };

  const handleAccept = async () => {
    try {
      console.log('Client data:', client); // Debug log
      const isOrgReferral = client?.referralCategory === 'organisation';
      const isFreeService = (client?.sessionRate === 0 || client?.sessionRate === undefined);
      
      console.log('Payment check:', { isOrgReferral, isFreeService, sessionRate: client?.sessionRate, referralCategory: client?.referralCategory }); // Debug log
      
      if (isOrgReferral || isFreeService) {
        // No payment needed for organization referrals or free services
        await submitAcceptance();
      } else {
        // For paid services, show payment options
        setShowPaymentOptionsDialog(true);
      }
    } catch (err: any) {
      console.error('Error accepting contract:', err);
      const errorMessage = err.response?.data?.message || 'Failed to accept the contract. Please try again.';
      setError(errorMessage);
    }
  };

  const submitAcceptance = useCallback(async (stripeSessionId?: string) => {
    try {
      const isOrgReferral = client?.referralCategory === 'organisation';
      const isFreeService = (client?.sessionRate === 0 || client?.sessionRate === undefined);
      
      const response = await axios.post(`/api/contract/public/accept/${token}`, {
        stripeSessionId,
        // For org referrals or free services, mark payment as confirmed since no payment is needed
        paymentConfirmed: (isOrgReferral || isFreeService) ? true : undefined
      });
      
      // Check if there's a redirect URL in the response
      if (response.data.redirectUrl) {
        setAccepted(true);
        setError(null);
        window.location.href = response.data.redirectUrl;
      } else {
        // Fallback to default redirect
        setAccepted(true);
        setError(null);
        navigate('/contract-accepted', {
          state: {
            message: 'Thank you for accepting the contract. Your counselling journey can now begin.'
          }
        });
      }
    } catch (err: any) {
      console.error('Error submitting contract acceptance:', err);
      const errorMessage = err.response?.data?.message || 'Failed to accept the contract. Please try again.';
      setError(errorMessage);
    }
  }, [client, token, navigate, setAccepted]);

  const handlePaymentOptionSubmit = async () => {
    try {
      setLoading(true);

      // Store contract token for later use
      if (token) {
        localStorage.setItem('contractToken', token);
      }

      // Create checkout session
      const response = await axios.post('/api/payments/public/create-checkout-session', {
        clientId: client?._id,
        contractToken: token,
        paymentOption,
        numberOfSessions: paymentOption === 'block' ? numberOfSessions : undefined
      });

      const { sessionId } = response.data;
      const stripe = await loadStripe(stripePublicKey);
      if (!stripe) {
        throw new Error('Failed to load Stripe');
      }

      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) {
        throw error;
      }
    } catch (err: any) {
      console.error('Error creating payment session:', err);
      setError(err.response?.data?.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const handleStripeReturn = useCallback(async (sessionId: string) => {
    try {
      // Accept contract with Stripe session ID
      const response = await axios.post(`/api/contract/public/accept/${token}`, {
        stripeSessionId: sessionId
      });
      
      if (response.data.client) {
        setAccepted(true);
        setError(null);
        navigate('/contract-accepted', {
          state: {
            message: 'Thank you for accepting the contract and completing the payment. Your counselling journey can now begin.'
          }
        });
      }
    } catch (err: any) {
      console.error('Error processing payment confirmation:', err);
      const errorMessage = err.response?.data?.message || 'Failed to process payment confirmation. Please contact support.';
      setError(errorMessage);
    }
  }, [token, navigate, setAccepted]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      handleStripeReturn(sessionId);
    }
  }, [handleStripeReturn]);

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      setError('Please provide a reason for declining.');
      return;
    }

    try {
      await axios.post(`/api/contract/public/decline/${token}`, { declineReason });
      setDeclined(true);
      setShowDeclineDialog(false);
      // Show success message and navigate to a thank you page
      navigate('/contract-declined', { 
        state: { 
          message: 'Contract has been declined. Thank you for your response.' 
        } 
      });
    } catch (err: any) {
      console.error('Error declining contract:', err);
      const errorMessage = err.response?.data?.message || 'Failed to decline the contract. Please try again.';
      setError(errorMessage);
      setShowDeclineDialog(false);
    }
  };

  useEffect(() => {
    const handleSuccessfulPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const status = urlParams.get('status');
      const sessionId = urlParams.get('session_id');

      if (status === 'success' && sessionId) {
        try {
          // Submit contract acceptance with session ID
          await submitAcceptance(sessionId);
          navigate('/contract-accepted');
        } catch (err) {
          console.error('Error handling successful payment:', err);
          setError('Error completing contract acceptance. Please contact support.');
        }
      }
    };

    handleSuccessfulPayment();
  }, [navigate, submitAcceptance]);

  if (loading) {
    return (
      <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <Typography variant="h6" className="text-gray-700 mb-4">
          Loading your contract details...
        </Typography>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
      {client ? (
        <Box className="max-w-4xl mx-auto">
          <Paper elevation={0} className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <Box className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <Typography variant="h4" className="text-white font-light">
                Welcome, {client.firstName}!
              </Typography>
              <Typography variant="subtitle1" className="text-blue-100 mt-1">
                Please review your information and the counselling agreement below
              </Typography>
            </Box>

            <Box className="p-6">
              {/* Personal Information Section */}
              <Box className="mb-8">
                <Box className="flex justify-between items-center mb-4">
                  <Typography variant="h5" className="text-gray-800">
                    Personal Information
                  </Typography>
                  {!isEditing ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setIsEditing(true)}
                      className="hover:bg-blue-50"
                    >
                      Edit Details
                    </Button>
                  ) : (
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        className="mr-2"
                      >
                        Save Changes
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setIsEditing(false);
                          setEditedClient({
                            firstName: client.firstName,
                            lastName: client.lastName,
                            phone: client.phone,
                            gender: client.gender,
                            emergencyContactName: client.emergencyContactName,
                            emergencyContactPhone: client.emergencyContactPhone,
                            address: client.address,
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>

                <Grid container spacing={4}>
                  {isEditing ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="First Name"
                          value={editedClient.firstName || ''}
                          onChange={(e) => setEditedClient({ ...editedClient, firstName: e.target.value })}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Last Name"
                          value={editedClient.lastName || ''}
                          onChange={(e) => setEditedClient({ ...editedClient, lastName: e.target.value })}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Phone"
                          value={editedClient.phone || ''}
                          onChange={(e) => setEditedClient({ ...editedClient, phone: e.target.value })}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Gender</InputLabel>
                          <Select
                            value={editedClient.gender || ''}
                            onChange={(e) => setEditedClient({ ...editedClient, gender: e.target.value })}
                            label="Gender"
                          >
                            {genderOptions.map((option) => (
                              <MenuItem key={option._id} value={option.value}>
                                {option.displayName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Date of Birth
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel>Day</InputLabel>
                              <Select
                                value={day}
                                onChange={(e) => setDay(e.target.value)}
                                label="Day"
                              >
                                {Array.from({ length: 31 }, (_, i) => (
                                  <MenuItem key={i} value={String(i + 1).padStart(2, '0')}>
                                    {i + 1}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel>Month</InputLabel>
                              <Select
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                label="Month"
                              >
                                {Array.from({ length: 12 }, (_, i) => (
                                  <MenuItem key={i} value={String(i + 1).padStart(2, '0')}>
                                    {i + 1}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel>Year</InputLabel>
                              <Select
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                label="Year"
                              >
                                {Array.from({ length: 100 }, (_, i) => (
                                  <MenuItem key={i} value={String(new Date().getFullYear() - i)}>
                                    {new Date().getFullYear() - i}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Emergency Contact Name"
                          value={editedClient.emergencyContactName || ''}
                          onChange={(e) => setEditedClient({ ...editedClient, emergencyContactName: e.target.value })}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Emergency Contact Phone"
                          value={editedClient.emergencyContactPhone || ''}
                          onChange={(e) => setEditedClient({ ...editedClient, emergencyContactPhone: e.target.value })}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Address"
                          value={editedClient.address || ''}
                          onChange={(e) => setEditedClient({ ...editedClient, address: e.target.value })}
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" className="text-gray-600">Name</Typography>
                        <Typography variant="body1" className="text-gray-900">{client.firstName} {client.lastName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" className="text-gray-600">Email</Typography>
                        <Typography variant="body1" className="text-gray-900">{client.email}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" className="text-gray-600">Phone</Typography>
                        <Typography variant="body1" className="text-gray-900">{client.phone}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" className="text-gray-600">Date of Birth</Typography>
                        <Typography variant="body1" className="text-gray-900">{formatDOB(client.dob)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" className="text-gray-600">Gender</Typography>
                        <Typography variant="body1" className="text-gray-900">
                          {genderOptions.find(opt => opt.value === client.gender)?.displayName || 'Not specified'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" className="text-gray-600">Emergency Contact</Typography>
                        <Typography variant="body1" className="text-gray-900">{client.emergencyContactName}</Typography>
                        <Typography variant="body2" className="text-gray-700">{client.emergencyContactPhone}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" className="text-gray-600">Address</Typography>
                        <Typography variant="body1" className="text-gray-900">{client.address}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>

              {/* Contract Section */}
              {pdfUrl && (
                <Box className="mt-8">
                  <Typography variant="h5" className="text-gray-800 mb-4">
                    Counselling Agreement
                  </Typography>
                  <Paper elevation={0} className="border border-gray-200 rounded-lg overflow-hidden mb-6">
                    <iframe
                      src={pdfUrl}
                      className="w-full h-[800px]"
                      title="Contract PDF"
                    />
                  </Paper>

                  <Box className="flex justify-end gap-4">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAccept}
                      disabled={isEditing}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Accept Agreement
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setShowDeclineDialog(true)}
                      disabled={isEditing}
                      className="border-red-500 text-red-500 hover:bg-red-50"
                    >
                      Decline Agreement
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      ) : null}

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onClose={() => setShowDeclineDialog(false)}>
        <DialogTitle>
          <Typography variant="h6" className="text-gray-900">
            Decline Agreement
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Please tell us why you're declining"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions className="p-4">
          <Button onClick={() => setShowDeclineDialog(false)} className="text-gray-600">
            Cancel
          </Button>
          <Button onClick={handleDecline} color="error" variant="contained">
            Confirm Decline
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Options Dialog */}
      <Dialog 
        open={showPaymentOptionsDialog} 
        onClose={() => {
          setShowPaymentOptionsDialog(false);
          setError(null);
        }}
      >
        <DialogTitle>
          <Typography variant="h6" className="text-gray-900">
            Payment Options
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4">
            Please select your preferred payment option:
          </Typography>
          
          {client?.referralCategory === 'direct' && (
            <Alert severity="info" className="mb-4">
              A £15 assessment fee will be added to your payment.
            </Alert>
          )}
          
          <FormControl component="fieldset" className="mb-4">
            <RadioGroup
              value={paymentOption}
              onChange={(e) => setPaymentOption(e.target.value as 'session' | 'block')}
            >
              <FormControlLabel 
                value="session" 
                control={<Radio />} 
                label={client?.referralCategory === 'direct' 
                  ? `Pay per session (£${(client?.sessionRate || 0) + 15} including £15 assessment fee)`
                  : "Pay per session"
                } 
              />
              <FormControlLabel 
                value="block" 
                control={<Radio />} 
                label={client?.referralCategory === 'direct'
                  ? `Block booking (£${client?.sessionRate || 0} per session + £15 assessment fee)`
                  : "Block booking (2-6 sessions)"
                } 
              />
            </RadioGroup>
          </FormControl>
          
          {paymentOption === 'block' && (
            <FormControl fullWidth variant="outlined" className="mt-4">
              <InputLabel>Number of Sessions</InputLabel>
              <Select
                value={numberOfSessions}
                onChange={(e) => setNumberOfSessions(Number(e.target.value))}
                label="Number of Sessions"
              >
                {[2, 3, 4, 5, 6].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num} sessions
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {error && (
            <Alert severity="error" className="mt-4">
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions className="p-4">
          <Button 
            onClick={() => {
              setShowPaymentOptionsDialog(false);
              setError(null);
            }} 
            className="text-gray-600"
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePaymentOptionSubmit} 
            color="primary" 
            variant="contained"
          >
            Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContractPage;